// src/pages/MarketingAdmin.js
"use client";

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../styles/Marketing.css";
import { FaBullhorn, FaWhatsapp, FaImage, FaListUl, FaPaperPlane, FaSearch } from "react-icons/fa";

const API = "https://api.ticketdovaqueiro.com/api";

export default function MarketingAdmin() {
  const [eventos, setEventos] = useState([]);
  const [eventoId, setEventoId] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);

  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);

  const total = recipients.length;
  const token = useMemo(() => localStorage.getItem("token"), []);

  const isValidUrl = (u) => !u || /^https?:\/\/.{3,}/i.test(u);

  // carregar eventos
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/events`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setEventos(res.data || []);
      } catch (e) {
        console.error("Erro eventos:", e?.response?.data || e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  // preview de destinatários
  const handlePreview = async () => {
    if (!eventoId) {
      alert("Selecione um evento.");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API}/marketing/preview`, {
        params: { eventId: eventoId },
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setRecipients(res.data?.recipients || []);
      setPreviewVisible(true);
    } catch (e) {
      console.error("Erro preview:", e?.response?.data || e.message);
      alert("Erro ao montar preview.");
    } finally {
      setLoading(false);
    }
  };

  // enviar campanha (1/min fixo; jitter 0–20s)
  const handleEnviar = async () => {
    if (!eventoId) return alert("Selecione um evento.");
    if (!message?.trim()) return alert("Digite a mensagem.");
    if (imageUrl?.trim() && !isValidUrl(imageUrl)) return alert("URL da imagem inválida. Use http(s)://...");

    if (!window.confirm(`Enviar para ${total} contato(s), com taxa de 1 por minuto?`)) return;

    try {
      setLoading(true);
      const res = await axios.post(
        `${API}/marketing/enviar`,
        {
          eventId: eventoId,
          message,
          imageUrl: imageUrl?.trim() || undefined,
          delaySeconds: 60,   // 🔒 1/min (o back já força isso)
          jitterSeconds: 20,  // leve variação
          dryRun: false,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      alert(`Campanha disparada.\nEnviados: ${res.data?.enviados}/${res.data?.total}`);
    } catch (e) {
      console.error("Erro enviar:", e?.response?.data || e.message);
      alert("Erro ao enviar campanha.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="marketing-container">
      <h2 className="marketing-title"><FaBullhorn /> Campanhas WhatsApp</h2>

      <div className="form-group">
        <label>Evento</label>
        <select value={eventoId} onChange={(e) => setEventoId(e.target.value)} disabled={loading}>
          <option value="">Selecione um evento</option>
          {eventos.map((ev) => (
            <option key={ev._id} value={ev._id}>
              {ev.nome}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Mensagem (texto)</label>
        <textarea
          rows={5}
          placeholder="Escreva sua mensagem promocional..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <small className="hint">
          Dica: mantenha objetiva, com valor real para o cliente. Evite excesso de links/keywords.
        </small>
      </div>

      <div className="form-group">
        <label>Imagem (URL pública) — opcional</label>
        <div className="inline">
          <input
            type="url"
            placeholder="https://cdn.seuservidor.com/banner.png"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
          <span className="icon">
            <FaImage />
          </span>
        </div>
        <small className="hint">
          Use URL acessível publicamente. (Se precisar, depois integramos upload para seu /uploads.)
        </small>
      </div>

      <div className="grid-actions">
        <button className="btn-outline" onClick={handlePreview} disabled={!eventoId || loading}>
          <FaSearch /> Pré-visualizar contatos
        </button>
        <button
          className="btn-primary"
          onClick={handleEnviar}
          disabled={!eventoId || !message?.trim() || total === 0 || loading}
        >
          <FaWhatsapp /> Enviar (1/min + jitter)
        </button>
      </div>

      {previewVisible && (
        <>
          <div className="preview-card">
            <div className="preview-header">
              <FaListUl />
              <h3>Destinatários</h3>
              <span className="badge">{total}</span>
            </div>

            {total === 0 ? (
              <p className="muted">Nenhum contato elegível foi encontrado para este evento.</p>
            ) : (
              <ul className="preview-list">
                {recipients.map((r, i) => (
                  <li key={`${r}-${i}`}>{r}</li>
                ))}
              </ul>
            )}

            <div className="info">
              <FaPaperPlane />
              <span>
                Disparo será feito <strong>um por minuto</strong> com pequena variação aleatória para reduzir padrão de spam.
              </span>
            </div>
          </div>

          {/* === Pré-visualização de como a mensagem chega no WhatsApp === */}
          <div className="wa-preview">
            <div className="wa-preview-header">Pré-visualização</div>
            <div className="wa-chat">
              {/* IMAGEM primeiro (quando URL válida) */}
              {imageUrl?.trim() && isValidUrl(imageUrl) && (
                <div className="wa-bubble wa-bubble-image">
                  <img src={imageUrl} alt="Pré-visualização" />
                </div>
              )}
              {/* TEXTO depois */}
              {message?.trim() && (
                <div className="wa-bubble wa-bubble-text">
                  <pre>{message}</pre>
                </div>
              )}
              {/* Caso nada preenchido */}
              {!message?.trim() && !imageUrl?.trim() && (
                <div className="wa-empty">Sua imagem e texto aparecerão aqui.</div>
              )}
              {/* Alerta de URL inválida */}
              {imageUrl?.trim() && !isValidUrl(imageUrl) && (
                <div className="wa-warn">URL da imagem inválida. Use http(s)://...</div>
              )}
            </div>
            <div className="wa-footnote">
              Ordem de envio: <strong>imagem</strong> → <strong>texto</strong>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
